import { Injectable } from "@angular/core";
import {
  AngularFirestore,
  AngularFirestoreCollection,
  DocumentReference,
} from "@angular/fire/firestore";
import { Observable } from "rxjs";
import { map, take, switchMap } from "rxjs/operators";
import { combineLatest, of } from "rxjs";
import { AuthService } from "../auth/auth.service";
import {
  Installation,
  InstallationPlace,
  Business,
  Checkout,
  SubscriptionPackages,
  SubscriptionAssigned,
  Schedule,
  InstallationArea,
  InstallationModule,
  PatrolZone,
  Zone,
  installationCounters,
} from "../../interfaces/installation";
import { Homeowner, Subscription, User } from "src/app/interfaces/user";
import { firestore } from "firebase/app";
import { Scanner } from "src/app/interfaces/terminal";
import { Access, CouponAssociate, Guest } from "src/app/interfaces/access";
import { ClassroomAnnouncement, Message } from "src/app/interfaces/school";
import { ManagerPoll, MessageConfig } from "src/app/interfaces/support";
import { DataReference } from "src/app/interfaces/helpers";
import { ContactsLabel } from "src/app/interfaces/contact";
import { NavController } from "@ionic/angular";
import { SiselseService } from "../siselse/siselse.service";
import { UsersService } from "../users/users.service";

@Injectable({
  providedIn: "root",
})
export class InstallationsManagerService {
  private installationsCollection: AngularFirestoreCollection<Installation>;
  private installations: Observable<Installation[]>;
  public installationsData: Installation[];
  public homeownersCollection: AngularFirestoreCollection<Homeowner>;
  public homeownersPlaceCollection: AngularFirestoreCollection<Homeowner>;
  public subscriptionsPlaceCollection: AngularFirestoreCollection<Subscription>;
  public businessCollection: AngularFirestoreCollection<Business>;
  public homeownersDataTemp: Homeowner[];
  public businessData: Business[];
  private managerPlacesCollection: AngularFirestoreCollection<InstallationPlace>;
  private managerAreasCollection: AngularFirestoreCollection<InstallationArea>;
  private scannersCollection: AngularFirestoreCollection<Scanner>;
  public scannersData: Scanner[];
  public installationSelect: Installation = null;
  public messages: Message[];

  public currentCoupon: Access;

  public installationType = {
    residential: false,
    company: false,
    parking: false,
    store: false,
    school: false,
  };

  public messageConfigSelect: MessageConfig = null;
  installationLabels: ContactsLabel[];

  constructor(
    private afs: AngularFirestore,
    private navCtrl: NavController,
    private authServ: AuthService,
    private siselseServ: SiselseService,
    private userServ: UsersService,
  ) {
    const userDocRef = this.afs.firestore
      .collection("users")
      .doc(this.authServ.getUserUID);
    this.installationsCollection = this.afs.collection<Installation>(
      "installations",
      (ref) => {
        return ref
          .where("managers", "array-contains", userDocRef)
          .orderBy("name", "asc");
      },
    );

    // Obtener instalaciones donde el usuario es manager
    const managerInstallations$ = this.installationsCollection
      .snapshotChanges()
      .pipe(
        map((actions) => {
          return actions.map((a) => {
            const data = a.payload.doc.data() as Installation;
            const id = a.payload.doc.id;
            return { id, ...data };
          });
        }),
      );

    // Obtener las zonas donde el usuario pertenece
    const zonesCollection = this.afs.collection<Zone>("zones", (ref) =>
      ref.where("usersRef", "array-contains", userDocRef),
    );

    const zoneInstallations$ = zonesCollection.snapshotChanges().pipe(
      switchMap((actions) => {
        const zoneInstallationRefs = actions
          .map((a) => a.payload.doc.data() as Zone)
          .map((zone) => zone.installationsRef || [])
          .reduce((acc, val) => acc.concat(val), []);

        // Si no hay instalaciones, devolver un observable vacío
        if (zoneInstallationRefs.length === 0) {
          return of([]);
        }

        // Obtener las instalaciones de las referencias
        return combineLatest(
          zoneInstallationRefs.map((ref) =>
            this.afs.doc<Installation>(ref.path).valueChanges(),
          ),
        ).pipe(
          map((installations) =>
            installations.filter((installation) => !!installation),
          ), // Filtrar nulos
        );
      }),
    );

    // Combinar las instalaciones donde el usuario es manager con las instalaciones de las zonas
    this.installations = combineLatest([
      managerInstallations$,
      zoneInstallations$,
    ]).pipe(
      map(([managerInstallations, zoneInstallations]) => {
        const allInstallations = [
          ...managerInstallations,
          ...zoneInstallations,
        ];
        // Eliminar duplicados
        const uniqueInstallations = allInstallations.reduce((acc, current) => {
          const x = acc.find((item) => item.id === current.id);
          if (!x) {
            return acc.concat([current]);
          } else {
            return acc;
          }
        }, []);
        return uniqueInstallations;
      }),
    );
  }

  // INSTALLATIONS

  getInstallations(): Observable<Installation[]> {
    return this.installations;
  }

  getInstallation(id: string): Observable<Installation> {
    return this.installations.pipe(
      map((installations) =>
        installations.find((installation) => installation.id === id),
      ),
      take(1),
    );
  }

  updateInstallation(installation: Installation): Promise<void> {
    return this.installationsCollection
      .doc(installation.id)
      .set(installation, { merge: true });
  }

  filterInstallations(filter: string): Observable<Installation[]> {
    filter = filter.toLowerCase();
    if (filter.length === 0) {
      return this.installationsCollection
        .valueChanges()
        .pipe(map((items) => items.filter((item) => item.name !== "")));
    }
    const strFrontCode = filter.slice(0, filter.length - 1);
    const strEndCode = filter.slice(filter.length - 1, filter.length);

    const startCode = filter;
    const endCode =
      strFrontCode + String.fromCharCode(strEndCode.charCodeAt(0) + 1);

    // return this.installationsCollection.ref.where('name', '>=', startCode).where('name', '<', endCode).get();

    return this.installationsCollection
      .valueChanges()
      .pipe(
        map((items) =>
          items.filter(
            (item) =>
              (item.name >= startCode && item.name <= endCode) ||
              item.name.includes(filter),
          ),
        ),
      );
  }

  getInstallationPackages(id: string): Observable<SubscriptionPackages[]> {
    const limit = new Date();
    limit.setMonth(limit.getMonth() - 6);
    return this.installationsCollection
      .doc<Installation>(id)
      .collection("subscriptions_packages", (ref) => {
        return ref
          .where("date.final", ">=", limit)
          .orderBy("date.final", "desc");
      })
      .valueChanges();
  }

  updateInstallationPackage(
    p: SubscriptionPackages,
    subAssigned: SubscriptionAssigned,
  ) {
    p.subscriptionsRemainder =
      p.subscriptionsTotal - p.subscriptionsAssigned.length - 1;
    return this.afs.firestore
      .collection("installations")
      .doc(this.installationSelect.id)
      .collection("subscriptions_packages")
      .doc(p.id)
      .update({
        subscriptionsRemainder: p.subscriptionsRemainder,
        subscriptionsAssigned: firestore.FieldValue.arrayUnion(subAssigned),
      });
  }

  generateInstallationMessageConfig(
    name: string,
    id: string,
    modules?: InstallationModule[],
    patrolZones?: PatrolZone[],
  ) {
    return {
      id,
      installation: {
        name,
        ref: this.afs.doc("installations/" + id).ref,
      },
      modules,
      patrolZones: patrolZones || [],
      documents: [],
    };
  }

  getInstallationMessageConfig(id: string): Observable<MessageConfig[]> {
    return this.afs
      .collection<MessageConfig>("messages", (ref) =>
        ref.where("id", "==", id).limit(1),
      )
      .valueChanges();
  }

  updateMessageConfig(messageConfig: MessageConfig): Promise<void> {
    return this.afs
      .collection("messages")
      .doc(messageConfig.id)
      .set(messageConfig, { merge: true });
  }

  getSipServers(
    installationId: string,
    collection: "school" | "residential",
  ): Observable<Message[]> {
    return this.afs
      .collection("messages")
      .doc(installationId)
      .collection<Message>(collection, (ref) => {
        return ref.where("status", "==", "open").orderBy("ts", "desc");
      })
      .valueChanges();
  }

  // MANAGE PARKING

  getInstallationCheckouts(id: string): Observable<Checkout[]> {
    return this.installationsCollection
      .doc<Installation>(id)
      .collection("checkouts", (ref) => {
        return ref.orderBy("date.final", "desc").limit(5);
      })
      .valueChanges();
  }

  getInstallationCheckoutsByDate(
    id: string,
    start: Date,
    final: Date,
    limit?: number,
  ): Observable<Checkout[]> {
    return this.installationsCollection
      .doc<Installation>(id)
      .collection("checkouts", (ref) => {
        return ref
          .where("date.final", ">=", new Date(start))
          .where("date.final", "<=", new Date(final))
          .orderBy("date.final", "desc")
          .limit(limit);
      })
      .valueChanges();
  }

  getAffiliateCheckoutsByDate(
    installationId: string,
    AffiliateId: string,
    start: Date,
    final: Date,
    limit?: number,
  ): Observable<Checkout[]> {
    return this.installationsCollection
      .doc<Installation>(installationId)
      .collection("checkouts_partners", (ref) => {
        return ref
          .where("date.start", ">=", new Date(start))
          .where(
            "businessRef",
            "==",
            this.afs.firestore
              .collection("installations")
              .doc(installationId)
              .collection("business")
              .doc(AffiliateId),
          )
          .orderBy("date.start")
          .limit(limit);
      })
      .valueChanges()
      .pipe(
        map((checkouts) =>
          checkouts.filter((checkout) => checkout.date.final <= final),
        ),
      );
  }

  getAccessParking(id: string) {
    const limit = new Date();
    limit.setDate(limit.getDate() - 31);
    return this.afs
      .collection<Access>("access", (ref) => {
        return ref
          .where(
            "installationRef",
            "==",
            this.afs.firestore.collection("installations").doc(id),
          )
          .where("type", "==", "parking")
          .where("date.final", ">", limit)
          .orderBy("date.final", "desc")
          .limit(200);
      })
      .valueChanges();
  }

  getAccessParkingActives(id: string) {
    const limit = new Date();
    limit.setDate(limit.getDate() - 31);
    return this.afs
      .collection<Access>("access", (ref) => {
        return ref
          .where(
            "installationRef",
            "==",
            this.afs.firestore.collection("installations").doc(id),
          )
          .where("type", "==", "parking")
          .where("date.start", ">", limit)
          .where("date.final", "==", null)
          .orderBy("date.start", "desc");
      })
      .valueChanges();
  }

  getParkingSoldTicketsByFinalDate(
    installationId: string,
    finalDate: Date,
  ): Observable<Access[]> {
    const start = new Date(finalDate);
    start.setHours(0, 0, 0, 0);

    const end = new Date(finalDate);
    end.setHours(23, 59, 59, 999);

    return this.afs
      .collection<Access>("access", (ref) => {
        return ref
          .where(
            "installationRef",
            "==",
            this.afs.firestore.collection("installations").doc(installationId),
          )
          .where("type", "==", "parking")
          .where("date.final", ">=", start)
          .where("date.final", "<=", end)
          .orderBy("date.final", "desc");
      })
      .valueChanges();
  }

  getParkingDiscounts(
    installationID: string,
    start: Date,
    final: Date,
    limit?: number,
  ) {
    return this.afs
      .collection<Access>("access", (ref) => {
        return ref
          .where(
            "installationRef",
            "==",
            this.afs.firestore.collection("installations").doc(installationID),
          )
          .where("type", "==", "parking")
          .where("ticket.discounted", "array-contains", installationID)
          .where("date.start", ">=", new Date(start))
          .where("date.start", "<=", new Date(final))
          .orderBy("date.start", "desc")
          .limit(limit);
      })
      .valueChanges();
  }

  getParkingBusinessDiscounts(
    installationID: string,
    start: Date,
    final: Date,
    limit?: number,
    businessID?: string,
  ) {
    return this.afs
      .collection<Access>("access", (ref) => {
        return ref
          .where(
            "installationRef",
            "==",
            this.afs.firestore.collection("installations").doc(installationID),
          )
          .where("type", "==", "parking")
          .where("date.start", ">=", new Date(start))
          .where("date.start", "<=", new Date(final))
          .where(
            "ticket.discounted",
            "array-contains",
            businessID ? businessID : installationID,
          )
          .orderBy("date.start", "desc")
          .limit(limit);
      })
      .valueChanges();
  }

  cancelTicket(ticket: Access): Promise<void> {
    ticket.delete = firestore.Timestamp.fromDate(new Date());
    return this.afs.firestore.collection("access").doc(ticket.id).set(ticket);
  }

  // MANAGE BUSINESSES

  getInstallationBusinesses(id: string): Observable<Business[]> {
    this.businessCollection = this.installationsCollection
      .doc<Installation>(id)
      .collection("business");
    return this.businessCollection.valueChanges();
  }

  updateBusiness(bp: Business): Promise<void> {
    return this.businessCollection.doc(bp.id).update(bp);
  }

  addBusiness(bp: Business): Promise<void> {
    bp.id = this.afs.createId();
    return this.businessCollection.doc(bp.id).set(bp);
  }

  getBusiness(id: string): Observable<Business> {
    return this.businessCollection
      .doc<Business>(id)
      .valueChanges()
      .pipe(
        take(1),
        map((business) => {
          business.id = id;
          return business;
        }),
      );
  }

  filterBusiness(filter: string): Observable<Business[]> {
    filter = filter.toLowerCase();
    if (filter.length === 0) {
      return this.businessCollection
        .valueChanges()
        .pipe(map((items) => items.filter((item) => item.name !== "")));
    }
    const strFrontCode = filter.slice(0, filter.length - 1);
    const strEndCode = filter.slice(filter.length - 1, filter.length);

    const startCode = filter;
    const endCode =
      strFrontCode + String.fromCharCode(strEndCode.charCodeAt(0) + 1);

    return this.businessCollection
      .valueChanges()
      .pipe(
        map((items) =>
          items.filter(
            (item) =>
              (item.name >= startCode && item.name <= endCode) ||
              item.name.includes(filter),
          ),
        ),
      );
  }

  // MANAGE SUBS

  managerSubsByPlace(placesDocId: string) {
    const today = new Date();
    const placeDocRef = this.afs.firestore
      .collection("places")
      .doc(placesDocId);
    this.subscriptionsPlaceCollection = this.afs.collection<Subscription>(
      "subscriptions",
      (ref) => {
        return ref
          .where("placeRef", "==", placeDocRef)
          .where("date.final", ">=", today)
          .where("userRef", "==", null)
          .orderBy("date.final")
          .orderBy("created", "desc");
      },
    );
    return this.subscriptionsPlaceCollection.valueChanges();
  }

  updateSubscription(sub: Subscription, homeowner: Homeowner): Promise<void> {
    sub.homeownerRef = this.afs.firestore
      .collection("homeowners")
      .doc(homeowner.id);
    return this.subscriptionsPlaceCollection
      .doc(sub.id)
      .set(sub, { merge: true });
  }
  updateSubscriptionHomeowner(
    sub: Subscription,
    homeowner: Homeowner,
  ): Promise<void> {
    homeowner.subscriptionRef = this.afs.firestore
      .collection("subscriptions")
      .doc(sub.id);
    homeowner.subscriptionName = sub.name;
    return this.afs.firestore
      .collection("homeowners")
      .doc(homeowner.id)
      .set(homeowner, { merge: true });
  }

  // MANAGE RESIDENTS

  managerHomeowners(installationDocId: string, changes: boolean = true) {
    const installationDocRef = this.afs.firestore
      .collection("installations")
      .doc(installationDocId);
    this.homeownersCollection = this.afs.collection<Homeowner>(
      "homeowners",
      (ref) => {
        return ref
          .where("installationRef", "==", installationDocRef)
          .orderBy("userName", "asc");
      },
    );
    if (!changes) return this.homeownersCollection.valueChanges().pipe(take(1));
    return this.homeownersCollection.valueChanges();
  }

  managerHomeownersByPlace(placesDocId: string) {
    const placeDocRef = this.afs.firestore
      .collection("places")
      .doc(placesDocId);
    this.homeownersPlaceCollection = this.afs.collection<Homeowner>(
      "homeowners",
      (ref) => {
        return ref
          .where("placeRef", "==", placeDocRef)
          .orderBy("userName", "asc");
      },
    );
    return this.homeownersPlaceCollection.valueChanges().pipe(take(1));
  }

  reportOnHomeowners(installationDocId: string) {
    const installationDocRef = this.afs.firestore
      .collection("installations")
      .doc(installationDocId);
    this.homeownersCollection = this.afs.collection<Homeowner>(
      "homeowners",
      (ref) => {
        return ref
          .where("installationRef", "==", installationDocRef)
          .orderBy("subscriptionFinish", "asc");
      },
    );
    return this.homeownersCollection.get();
  }

  deleteHomeowner(ho: Homeowner) {
    return this.afs.collection("homeowners").doc(ho.id).delete();
  }

  deleteShortcutsHomeowner(ho: Homeowner) {
    if (ho.shortcuts) delete ho.shortcuts;
    return this.afs.collection("homeowners").doc(ho.id).set(ho);
  }

  updateHomeowner(
    ho: Homeowner,
    updateAction?: "ACTIVATE" | "BLOCK" | "RESTORE",
  ) {
    if (ho.defaulter === undefined) {
      ho.defaulter = false;
    }
    if (ho.hidden === undefined) {
      ho.hidden = false;
    }
    if (ho.restrictions === undefined) {
      ho.restrictions = {
        invitationLimit: null,
        reusableInvitation: true,
        qrOnly: false,
        qrOnlyInvitation: false,
        geolocation: false,
        distanceLimit: null,
        defaulter: null,
      };
    }
    let status = updateAction || (!ho.defaulter ? "ACTIVATE" : "BLOCK");

    const newUpdate = {
      userRef: this.authServ.userRef,
      userName: this.authServ.user.displayName,
      updateCode: this.authServ.user.displayName
        ? `${status.toUpperCase()}_${Date.now()}_${this.authServ.user.displayName
            .trim()
            .replace(/\s+/g, "_")
            .slice(0, 3)
            .toUpperCase()}`
        : `SYS_${status.toUpperCase()}_${Date.now()}`,
      ts: firestore.Timestamp.fromDate(new Date()),
    };

    // Primero obtener el documento para leer el array actual
    return this.afs.firestore
      .collection("homeowners")
      .doc(ho.id)
      .get()
      .then((doc) => {
        const currentUpdates = doc.data()?.update || [];

        // Agregar el nuevo update al final
        const updatedArray = [...currentUpdates, newUpdate];

        // Mantener solo los últimos 20 registros (eliminando los del inicio)
        const limitedUpdates = updatedArray.slice(-20);

        // Actualizar el documento con el array limitado
        return this.afs.firestore
          .collection("homeowners")
          .doc(ho.id)
          .update({
            placeName: ho.placeName,
            installationName: ho.installationName,
            defaulter: ho.defaulter,
            hidden: ho.hidden ?? false,
            deleted: ho.deleted ?? null,
            restrictions: {
              invitationLimit: ho.restrictions.invitationLimit ?? null,
              reusableInvitation: ho.restrictions.reusableInvitation ?? true,
              distanceLimit: ho.restrictions.distanceLimit ?? null,
              geolocation: ho.restrictions.geolocation ?? false,
              qrOnly: ho.restrictions.qrOnly ?? false,
              qrOnlyInvitation: ho.restrictions.qrOnlyInvitation ?? false,
              default: ho.restrictions.defaulter ?? null,
            },
            update: limitedUpdates,
          });
      });
  }

  updateRestrictions(homeowner: Homeowner): Promise<void> {
    const updateData: any = {
      restrictions: {
        invitationLimit: homeowner.restrictions?.invitationLimit ?? null,
        reusableInvitation: homeowner.restrictions?.reusableInvitation ?? true,
        geolocation: homeowner.restrictions?.geolocation ?? false,
        distanceLimit: homeowner.restrictions?.distanceLimit ?? null,
        qrOnly: homeowner.restrictions?.qrOnly ?? false,
        qrOnlyInvitation: homeowner.restrictions?.qrOnlyInvitation ?? false,
        defaulter: homeowner.restrictions?.defaulter ?? null,
      },
    };

    // Si tiene installationData, también lo actualiza
    if (homeowner.installationData) {
      updateData.installationData = homeowner.installationData;
    }

    return this.homeownersCollection.doc(homeowner.id).update(updateData);
  }

  updateDisabled(homeowner: Homeowner): Promise<void> {
    return this.homeownersCollection.doc(homeowner.id).update({
      installationDisabled: homeowner.installationDisabled,
    });
  }

  filterHomeowners(filter: string): Observable<Homeowner[]> {
    filter = filter.toLowerCase();
    if (filter.length === 0) {
      return this.homeownersCollection
        .valueChanges()
        .pipe(
          map((items) =>
            items.filter((item) => (item = item) && !item.deleted),
          ),
        );
    }
    const strFrontCode = filter.slice(0, filter.length - 1);
    const strEndCode = filter.slice(filter.length - 1, filter.length);

    const startCode = filter;
    const endCode =
      strFrontCode + String.fromCharCode(strEndCode.charCodeAt(0) + 1);

    return this.homeownersCollection
      .valueChanges()
      .pipe(
        map((items) =>
          items.filter(
            (item) =>
              ((item.userName.toLowerCase() >= startCode &&
                item.userName.toLowerCase() <= endCode) ||
                (item.placeName.toLowerCase() >= startCode &&
                  item.placeName.toLowerCase() <= endCode) ||
                item.placeName.toLowerCase().includes(filter)) &&
              !item.deleted,
          ),
        ),
      );
  }

  filterHomeownersByPlace(filter: string): Observable<Homeowner[]> {
    filter = filter.toLowerCase();
    if (filter.length === 0) {
      return this.homeownersPlaceCollection
        .valueChanges()
        .pipe(
          map((items) =>
            items.filter((item) => (item = item) && !item.deleted),
          ),
        );
    }
    const strFrontCode = filter.slice(0, filter.length - 1);
    const strEndCode = filter.slice(filter.length - 1, filter.length);

    const startCode = filter;
    const endCode =
      strFrontCode + String.fromCharCode(strEndCode.charCodeAt(0) + 1);

    return this.homeownersCollection
      .valueChanges()
      .pipe(
        map((items) =>
          items.filter(
            (item) =>
              ((item.userName.toLowerCase() >= startCode &&
                item.userName.toLowerCase() <= endCode) ||
                (item.placeName.toLowerCase() >= startCode &&
                  item.placeName.toLowerCase() <= endCode) ||
                item.placeName.toLowerCase().includes(filter)) &&
              !item.deleted,
          ),
        ),
      );
  }

  // MANAGE SCANNERS
  updateScanner(sc: Scanner): Promise<void> {
    return this.scannersCollection.doc(sc.id).update(sc);
  }

  managerScanners(installationDocId: string) {
    const installationDocRef = this.afs.firestore
      .collection("installations")
      .doc(installationDocId);
    this.scannersCollection = this.afs.collection<Scanner>(
      "scanners",
      (ref) => {
        return ref
          .where("installationRef", "==", installationDocRef)
          .orderBy("userName", "asc")
          .limit(100);
      },
    );
    return this.scannersCollection.valueChanges();
  }

  addScanner(scanner: Scanner): Promise<void> {
    scanner.id = this.afs.createId();
    return this.scannersCollection.doc(scanner.id).set(scanner);
  }

  deleteScanner(scanner: Scanner): Promise<void> {
    return this.scannersCollection.doc(scanner.id).delete();
  }

  filterScanners(filter: string): Observable<Scanner[]> {
    filter = filter.toLowerCase();
    if (filter.length === 0) {
      return this.scannersCollection
        .valueChanges()
        .pipe(map((items) => items.filter((item) => (item = item))));
    }
    const strFrontCode = filter.slice(0, filter.length - 1);
    const strEndCode = filter.slice(filter.length - 1, filter.length);

    const startCode = filter;
    const endCode =
      strFrontCode + String.fromCharCode(strEndCode.charCodeAt(0) + 1);

    // return this.installationsCollection.ref.where('name', '>=', startCode).where('name', '<', endCode).get();

    return this.scannersCollection
      .valueChanges()
      .pipe(
        map((items) =>
          items.filter(
            (item) =>
              (item.userName.toLowerCase() >= startCode &&
                item.userName.toLowerCase() <= endCode) ||
              item.userName.includes(filter),
          ),
        ),
      );
  }

  // MANAGE PLACES

  managerPlaces(installationDocId: string) {
    const installationDocRef = this.afs.firestore
      .collection("installations")
      .doc(installationDocId);
    this.managerPlacesCollection = this.afs.collection<InstallationPlace>(
      "places",
      (ref) => {
        return ref
          .where("installationRef", "==", installationDocRef)
          .orderBy("street", "asc")
          .orderBy("number", "asc");
      },
    );

    return this.managerPlacesCollection.valueChanges();
  }

  getInstallationIDAreas(installationDocId: string) {
    const installationDocRef = this.afs.firestore
      .collection("installations")
      .doc(installationDocId);
    this.managerAreasCollection = this.afs.collection<InstallationArea>(
      "areas",
      (ref) => {
        return ref.where("installationRef", "==", installationDocRef);
      },
    );
    return this.managerAreasCollection.valueChanges();
  }

  filterPlaces(filter: string): Observable<InstallationPlace[]> {
    filter = filter.toLowerCase();
    if (filter.length === 0) {
      return this.managerPlacesCollection
        .valueChanges()
        .pipe(map((items) => items.filter((item) => (item = item))));
    }
    const strFrontCode = filter.slice(0, filter.length - 1);
    const strEndCode = filter.slice(filter.length - 1, filter.length);
    const startCode = filter;
    const endCode =
      strFrontCode + String.fromCharCode(strEndCode.charCodeAt(0) + 1);

    return this.managerPlacesCollection
      .valueChanges()
      .pipe(
        map((items) =>
          items.filter(
            (item) =>
              (item.street.toLowerCase() >= startCode &&
                item.street.toLowerCase() <= endCode) ||
              (item.number.toLowerCase() >= startCode &&
                item.number.toLowerCase() <= endCode) ||
              item.street.includes(filter) ||
              item.number.includes(filter),
          ),
        ),
      );
  }

  // MANAGE SCHEDULE

  updateSchedule(
    installation: string,
    schedule: Schedule[],
    type: string,
  ): Promise<void> {
    if (type === "residential") {
      return this.updateResidentialSchedule(installation, schedule);
    } else if (type === "parking") {
      return this.updateParkingSchedule(installation, schedule);
    } else if (type === "company") {
      return this.updateCompanySchedule(installation, schedule);
    } else if (type === "school") {
      return this.updateSchoolSchedule(installation, schedule);
    }
  }

  updateResidentialSchedule(
    installation: string,
    schedule: Schedule[],
  ): Promise<void> {
    return this.afs.firestore
      .collection("installations")
      .doc(installation)
      .update({
        "residential.schedule":
          schedule != null ? schedule : firestore.FieldValue.delete(),
      });
  }

  updateParkingSchedule(
    installation: string,
    schedule: Schedule[],
  ): Promise<void> {
    return this.afs.firestore
      .collection("installations")
      .doc(installation)
      .update({
        "parking.schedule":
          schedule != null ? schedule : firestore.FieldValue.delete(),
      });
  }

  updateCompanySchedule(
    installation: string,
    schedule: Schedule[],
  ): Promise<void> {
    return this.afs.firestore
      .collection("installations")
      .doc(installation)
      .update({
        "company.schedule":
          schedule != null ? schedule : firestore.FieldValue.delete(),
      });
  }

  updateSchoolSchedule(
    installation: string,
    schedule: Schedule[],
  ): Promise<void> {
    return this.afs.firestore
      .collection("installations")
      .doc(installation)
      .update({
        "school.schedule":
          schedule != null ? schedule : firestore.FieldValue.delete(),
      })
      .then(() => {
        this.updateMessageConfigSchedule(installation, schedule);
      });
  }

  updateMessageConfigSchedule(installation: string, schedule: Schedule[]) {
    return this.afs.firestore
      .collection("messages")
      .doc(installation)
      .update({
        "school.schedule":
          schedule != null ? schedule : firestore.FieldValue.delete(),
      });
  }

  // MANAGE SCHOOLS

  addAnnouncement(installationId: string, announcement: ClassroomAnnouncement) {
    return this.installationsCollection
      .doc(installationId)
      .collection("announcements")
      .doc(announcement.id)
      .set(announcement);
  }

  updateAnnouncement(
    installationId: string,
    announcement: ClassroomAnnouncement,
  ) {
    return this.installationsCollection
      .doc(installationId)
      .collection("announcements")
      .doc(announcement.id)
      .update(announcement);
  }

  deleteAnnouncement(id: string, installationId: string): Promise<void> {
    return this.installationsCollection
      .doc(installationId)
      .collection("announcements")
      .doc(id)
      .delete();
  }

  getAnnouncement(
    id: string,
    installationId: string,
  ): Observable<ClassroomAnnouncement> {
    return this.afs
      .collection<Scanner>("installations")
      .doc(installationId)
      .collection("announcements")
      .doc<ClassroomAnnouncement>(id)
      .valueChanges()
      .pipe(
        take(1),
        map((announcement) => {
          announcement.id = id;
          return announcement;
        }),
      );
  }

  getAnnouncements(
    installationId: string,
  ): Observable<ClassroomAnnouncement[]> {
    return this.afs
      .collection<Scanner>("installations")
      .doc(installationId)
      .collection("announcements", (ref) => {
        return ref.orderBy("created.ts", "desc");
      })
      .valueChanges();
  }

  filterAnnouncements(
    filter: string,
    installationId,
  ): Observable<ClassroomAnnouncement[]> {
    filter = filter.toLowerCase();
    if (filter.length === 0) {
      return this.installationsCollection
        .doc(installationId)
        .collection("announcements")
        .valueChanges()
        .pipe(map((items) => items.filter((item) => (item = item))));
    }
    const strFrontCode = filter.slice(0, filter.length - 1);
    const strEndCode = filter.slice(filter.length - 1, filter.length);
    const startCode = filter;
    const endCode =
      strFrontCode + String.fromCharCode(strEndCode.charCodeAt(0) + 1);

    return this.installationsCollection
      .doc(installationId)
      .collection("announcements")
      .valueChanges()
      .pipe(
        map((items) =>
          items.filter(
            (item) =>
              (item.title.toLowerCase() >= startCode &&
                item.title.toLowerCase() <= endCode) ||
              (item.created?.userName.toLowerCase() >= startCode &&
                item.created?.userName.toLowerCase() <= endCode) ||
              item.title.includes(filter) ||
              item.created?.userName.includes(filter),
          ),
        ),
      );
  }

  async loadMessages() {
    let messages: Message[] = [];
    const installations = await this.installations.toPromise();
    installations.forEach(async (installation) => {
      const msgs = await this.getMessages(
        installation.id,
        "residential",
      ).toPromise();
      messages = messages.concat(msgs);
    });
    console.log(messages);
  }

  getMessage(
    id: string,
    installationId: string,
    collection: "school" | "residential",
  ): Observable<Message> {
    return this.afs
      .collection("messages")
      .doc(installationId)
      .collection(collection)
      .doc<Message>(id)
      .valueChanges()
      .pipe(
        take(1),
        map((message) => {
          message.id = id;
          return message;
        }),
      );
  }

  getMessages(
    installationId: string,
    collection: "school" | "residential",
  ): Observable<Message[]> {
    return this.afs
      .collection("messages")
      .doc(installationId)
      .collection<Message>(collection, (ref) => {
        return ref.where("status", "==", "open").orderBy("ts", "desc");
      })
      .valueChanges();
  }

  updateMessage(
    installationId: string,
    collection: "school" | "residential",
    message: Message,
  ) {
    return this.afs
      .collection("messages")
      .doc(installationId)
      .collection(collection)
      .doc(message.id)
      .update(message);
  }

  filterMessages(filter: string, installationId): Observable<Message[]> {
    filter = filter.toLowerCase();
    if (filter.length === 0) {
      return this.afs
        .collection("messages")
        .doc(installationId)
        .collection<Message>("school")
        .valueChanges()
        .pipe(map((items) => items.filter((item) => (item = item))));
    }
    const strFrontCode = filter.slice(0, filter.length - 1);
    const strEndCode = filter.slice(filter.length - 1, filter.length);
    const startCode = filter;
    const endCode =
      strFrontCode + String.fromCharCode(strEndCode.charCodeAt(0) + 1);

    return this.afs
      .collection("messages")
      .doc(installationId)
      .collection<Message>("school")
      .valueChanges()
      .pipe(
        map((items) =>
          items.filter(
            (item) =>
              (item.topic.toLowerCase() >= startCode &&
                item.topic.toLowerCase() <= endCode) ||
              (item.tutor?.name.toLowerCase() >= startCode &&
                item.tutor?.name.toLowerCase() <= endCode),
          ),
        ),
      );
  }

  getPolls(installationId: string): Observable<ManagerPoll[]> {
    const monthAgo = new Date();
    monthAgo.setMonth(monthAgo.getMonth() - 1);
    return this.afs
      .collection("messages")
      .doc(installationId)
      .collection<ManagerPoll>("polls", (ref) => {
        return ref.where("ends", ">=", monthAgo).orderBy("ends", "desc");
      })
      .valueChanges();
  }

  getPoll(id: string, installation: string): Observable<ManagerPoll> {
    return this.afs
      .collection("messages")
      .doc(installation)
      .collection("polls")
      .doc<ManagerPoll>(id)
      .valueChanges()
      .pipe(
        take(1),
        map((poll) => {
          poll.id = id;
          return poll;
        }),
      );
  }

  updatePoll(installation: string, poll: ManagerPoll) {
    return this.afs
      .collection("messages")
      .doc(installation)
      .collection("polls")
      .doc(poll.id)
      .set(poll);
  }

  deletePoll(id: string, installation: string): Promise<void> {
    return this.afs
      .collection("messages")
      .doc(installation)
      .collection("polls")
      .doc(id)
      .delete();
  }

  // MANAGE STORES

  getCoupon(id: string): Observable<Access[]> {
    return this.afs
      .collection<Access>("access", (ref) => {
        return ref.where("id", "==", id);
      })
      .valueChanges();
  }

  getCoupons(installationId: string): Observable<Access[]> {
    const dateLimit = new Date();
    dateLimit.setMonth(dateLimit.getMonth() - 2);
    dateLimit.setHours(0, 0, 0, 0);
    return this.afs
      .collection<Access>("access", (ref) => {
        return ref
          .where(
            "installationRef",
            "==",
            this.afs.firestore.collection("installations").doc(installationId),
          )
          .where("type", "==", "coupon")
          .where("date.final", ">=", dateLimit)
          .orderBy("date.final", "desc");
      })
      .valueChanges();
  }

  getCouponsByAssociate(associateId: string): Observable<Access[]> {
    const dateLimit = new Date();
    dateLimit.setMonth(dateLimit.getMonth() - 2);
    dateLimit.setHours(0, 0, 0, 0);
    return this.afs
      .collection<Access>("access", (ref) => {
        return ref
          .where("coupon.associateIds", "array-contains", associateId)
          .where("date.final", ">=", dateLimit)
          .orderBy("date.final", "desc");
      })
      .valueChanges();
  }

  getCouponsByClient(contactId: string): Observable<Access[]> {
    const dateLimit = new Date();
    dateLimit.setMonth(dateLimit.getMonth() - 2);
    dateLimit.setHours(0, 0, 0, 0);
    return this.afs
      .collection<Access>("access", (ref) => {
        return ref
          .where("coupon.contacts", "array-contains", contactId)
          .where("date.final", ">=", dateLimit)
          .orderBy("date.final", "desc");
      })
      .valueChanges();
  }

  getCouponContacts(couponId: string): Observable<Guest[]> {
    return this.afs
      .collection("access")
      .doc(couponId)
      .collection("contacts")
      .valueChanges();
  }

  private sanitizeFirestoreData(value: any): any {
    if (Array.isArray(value)) {
      return value
        .map((item) => this.sanitizeFirestoreData(item))
        .filter((item) => item !== undefined);
    }

    if (value && value.constructor === Object) {
      const sanitized = {};
      Object.keys(value).forEach((key) => {
        const cleanValue = this.sanitizeFirestoreData(value[key]);
        if (cleanValue !== undefined) {
          sanitized[key] = cleanValue;
        }
      });
      return sanitized;
    }

    return value;
  }

  addContactToCoupon(couponId: string, contact: Guest) {
    const cleanContact = this.sanitizeFirestoreData(contact);
    return this.afs
      .collection("access")
      .doc(couponId)
      .collection("contacts")
      .doc(contact.contactId)
      .set(cleanContact);
  }

  getCouponAssociates(couponId: string): Observable<CouponAssociate[]> {
    return this.afs
      .collection("access")
      .doc(couponId)
      .collection<CouponAssociate>("associates")
      .valueChanges();
  }

  addAssociateToCoupon(couponId: string, associate: CouponAssociate) {
    return this.afs
      .collection("access")
      .doc(couponId)
      .collection("associates")
      .doc(associate.associate.ref.id)
      .set(associate);
  }

  updateCouponContact(couponId: string, contact: Guest) {
    const cleanContact = this.sanitizeFirestoreData(contact);
    return this.afs
      .collection("access")
      .doc(couponId)
      .collection("contacts")
      .doc(contact.contactId)
      .update(cleanContact);
  }

  updateCouponAssociate(couponId: string, associate: CouponAssociate) {
    return this.afs
      .collection("access")
      .doc(couponId)
      .collection("associates")
      .doc(associate.associate.ref.id)
      .update(associate);
  }

  // MANAGER OUTSOURCING

  checkRoles(installation: Installation) {
    const roles = {
      manager: false,
      contractor: false,
      customer_support: false,
    };

    roles.customer_support = this.authServ.hasRoles(["customer_support"]);
    if (
      installation &&
      installation?.managers &&
      installation.managers.findIndex(
        (m) => m.id === this.authServ.userRef.id,
      ) >= 0
    ) {
      roles.manager = true;
    } else if (
      installation.contractors &&
      installation.contractors.findIndex(
        (m) => m.id === this.authServ.userRef.id,
      ) >= 0
    ) {
      roles.contractor = true;
    }
    return roles;
  }

  addContractorsToInstallation(
    installationId: string,
    contractors: DataReference[],
  ) {
    const refs = [];
    contractors.forEach((c) => {
      refs.push(c.ref);
    });
    return this.installationsCollection
      .doc(installationId)
      .update({ contractors: refs });
  }

  getContractorInstallations(): Observable<Installation[]> {
    return this.afs
      .collection<Installation>("installations", (ref) => {
        return ref
          .where("contractors", "array-contains", this.authServ.userRef)
          .orderBy("name", "asc");
      })
      .valueChanges();
  }

  addContactsLabel(installationId: string, label: ContactsLabel) {
    return this.installationsCollection
      .doc(installationId)
      .collection("labels")
      .doc(label.id)
      .set(label);
  }

  deleteContactsLabel(id: string, installationId: string): Promise<void> {
    return this.installationsCollection
      .doc(installationId)
      .collection("labels")
      .doc(id)
      .delete();
  }

  getContactsLabel(
    id: string,
    installationId: string,
  ): Observable<ContactsLabel> {
    return this.afs
      .collection<Installation>("installations")
      .doc(installationId)
      .collection("labels")
      .doc<ContactsLabel>(id)
      .valueChanges()
      .pipe(
        take(1),
        map((label) => {
          label.id = id;
          return label;
        }),
      );
  }

  getContactsLabels(installationId: string): Observable<ContactsLabel[]> {
    return this.afs
      .collection<Installation>("installations")
      .doc(installationId)
      .collection<ContactsLabel>("labels", (ref) => {
        return ref.orderBy("created.ts", "desc");
      })
      .valueChanges();
  }

  updateUser(user: User) {
    throw new Error("Method not implemented.");
  }

  checkInstallationSelect(id: string) {
    if (
      this.installationSelect === null ||
      this.installationSelect === undefined
    ) {
      this.navCtrl.navigateRoot("/manager/" + id);
      return 0;
    }
    this.siselseServ.installationSelect = this.installationSelect;
  }

  generateCounters(): installationCounters {
    const now = new Date();
    const currentCounters: installationCounters = {
      places: {
        all: 0,
        siselse: {
          active: false,
          linked: 0,
        },
        types: {
          classroom: 0,
          common: 0,
          condo: 0,
          external: 0,
          house: 0,
          store: 0,
          office: 0,
        },
        ts: firestore.Timestamp.fromDate(now),
      },
      homeowners: {
        all: 0,
        defaulters: 0,

        subscriptions: {
          expired: 0,
          active: 0,
          free: 0,
          without: 0,
        },
        ts: firestore.Timestamp.fromDate(now),
      },
    };

    return currentCounters;
  }

  generateCountersHomeowners(
    installation: Installation,
    homeowners: Homeowner[],
  ) {
    const now = new Date();
    const currentCounters: installationCounters =
      installation.counters ?? this.generateCounters();
    const oneDayAgo = new Date();
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);
    if (
      currentCounters.homeowners.ts.toDate() > oneDayAgo &&
      currentCounters.homeowners.all > 0
    )
      return;
    // Calcular nuevos contadores
    const newCounters = {
      ...currentCounters,
      homeowners: {
        all: homeowners.length,
        defaulters: 0,
        subscriptions: {
          expired: 0,
          active: 0,
          free: 0,
          without: 0,
        },
        ts: firestore.Timestamp.fromDate(now),
      },
    };
    homeowners.forEach((homeowner) => {
      // Contar defaulters
      if (homeowner.defaulter === true) {
        newCounters.homeowners.defaulters++;
      }

      // Contar suscripciones
      if (homeowner.subscriptionFinish) {
        if (homeowner.subscriptionFinish.toDate() > now) {
          newCounters.homeowners.subscriptions.active++;
        } else {
          newCounters.homeowners.subscriptions.expired++;
        }
      } else {
        newCounters.homeowners.subscriptions.without++;
      }
    });

    // Comparar si los contadores han cambiado
    const hasChanged =
      currentCounters.homeowners.all !== newCounters.homeowners.all ||
      currentCounters.homeowners.defaulters !==
        newCounters.homeowners.defaulters ||
      currentCounters.homeowners.subscriptions.active !==
        newCounters.homeowners.subscriptions.active ||
      currentCounters.homeowners.subscriptions.expired !==
        newCounters.homeowners.subscriptions.expired ||
      currentCounters.homeowners.subscriptions.without !==
        newCounters.homeowners.subscriptions.without;

    // Solo actualizar si hay cambios
    if (hasChanged) {
      this.updateInstallation({
        ...installation,
        counters: newCounters,
      });
    }

    return newCounters;
  }

  generateCountersPlaces(
    installation: Installation,
    places: InstallationPlace[],
  ) {
    const now = new Date();
    const currentCounters: installationCounters =
      installation.counters ?? this.generateCounters();

    // Si se actualizó hace menos de un día, no hace nada
    const oneDayAgo = new Date();
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);
    if (
      currentCounters.places.ts.toDate() > oneDayAgo &&
      currentCounters.places.all > 0
    )
      return;

    // Calcular nuevos contadores
    const newCounters = {
      ...currentCounters,
      places: {
        all: places.length,
        siselse: {
          active: installation.siselse?.active ?? false,
          linked: 0,
        },
        types: {
          classroom: 0,
          common: 0,
          condo: 0,
          external: 0,
          house: 0,
          store: 0,
          office: 0,
        },
        ts: firestore.Timestamp.fromDate(now),
      },
    };

    places.forEach((place) => {
      // Contar tipos de lugares
      if (place.type) {
        let mappedType = place.type;

        // Mapear "common area" a "common"
        if (place.type === "common area") {
          mappedType = "common";
        }

        // Verificar si el tipo mapeado existe en los contadores
        if (newCounters.places.types[mappedType] !== undefined) {
          newCounters.places.types[mappedType]++;
        }

        if (place.siselse && place.type === "house") {
          newCounters.places.siselse.linked++;
        }
      }
    });

    // Comparar si los contadores han cambiado
    const hasChanged =
      currentCounters.places.all !== newCounters.places.all ||
      currentCounters.places.types.classroom !==
        newCounters.places.types.classroom ||
      currentCounters.places.types.common !== newCounters.places.types.common ||
      currentCounters.places.types.condo !== newCounters.places.types.condo ||
      currentCounters.places.types.external !==
        newCounters.places.types.external ||
      currentCounters.places.types.house !== newCounters.places.types.house ||
      currentCounters.places.types.store !== newCounters.places.types.store ||
      currentCounters.places.types.office !== newCounters.places.types.office ||
      currentCounters.places.siselse.linked !==
        newCounters.places.siselse.linked;

    // Solo actualizar si hay cambios
    if (hasChanged) {
      this.updateInstallation({
        ...installation,
        counters: newCounters,
      });
    }
  }
}
